import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BookOpen, Heart, Umbrella, Car, Briefcase, ArrowLeft, CheckCircle2,
  Circle, ChevronRight, Trophy, Lock, Loader2, Play, Award, Target
} from "lucide-react";
import { authGet, isAuthenticated } from "../utils/auth";

const ICON_MAP = {
  foundation: BookOpen,
  heart: Heart,
  umbrella: Umbrella,
  car: Car,
  briefcase: Briefcase,
};

export default function TrainingModule() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated()) { navigate("/advisor/login"); return; }
    fetchModule();
  }, [moduleId]);

  const fetchModule = async () => {
    try {
      const res = await authGet("/api/training/modules");
      if (res.ok) {
        const mod = (res.data.modules || []).find(m => m.id === moduleId);
        if (mod) setModule(mod);
        else navigate("/advisor/training");
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#A87608] animate-spin" />
      </div>
    );
  }

  if (!module) return null;

  const Icon = ICON_MAP[module.icon] || BookOpen;
  const allChaptersDone = module.chapters.every(ch => ch.completed);
  const nextChapter = module.chapters.find(ch => !ch.completed);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8]">
      <div className="max-w-3xl mx-auto px-4 py-6 pb-32">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/advisor/training")} className="text-[#3A4557] hover:text-[#1B2A4A] transition-colors" data-testid="back-to-academy">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5" style={{ color: module.color }} />
            <h1 className="text-xl font-bold text-[#1B2A4A]">
              {module.title}
            </h1>
          </div>
        </div>

        {/* Module Header Card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-[#C9D4E6]/50 overflow-hidden mb-6"
          style={{ background: `linear-gradient(135deg, ${module.color}08, ${module.color}03)` }}>
          <div className="p-5">
            <p className="text-xs uppercase tracking-wider font-medium mb-1" style={{ color: module.color }}>
              Module {module.order} — {module.subtitle}
            </p>
            <p className="text-[#3A4557] text-sm mt-2">{module.description}</p>
            
            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex justify-between text-xs text-[#3A4557] mb-1.5">
                <span>{module.completed_chapters} of {module.total_chapters} chapters completed</span>
                <span className="font-medium" style={{ color: module.color }}>{module.progress_pct}%</span>
              </div>
              <div className="w-full bg-[#E2E9F4]/50 rounded-full h-2">
                <motion.div className="h-2 rounded-full" style={{ backgroundColor: module.color }}
                  initial={{ width: 0 }} animate={{ width: `${module.progress_pct}%` }} transition={{ duration: 0.6 }} />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Chapters List */}
        <div className="mb-6">
          <h2 className="text-sm font-medium text-[#3A4557] uppercase tracking-wider mb-3 px-1">Chapters</h2>
          <div className="space-y-2">
            {module.chapters.map((ch, idx) => {
              const isNext = nextChapter?.id === ch.id;
              return (
                <motion.div key={ch.id}
                  initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  onClick={() => navigate(`/advisor/training/${moduleId}/chapter/${ch.id}`)}
                  className={`group flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                    ch.completed
                      ? "bg-emerald-500/5 border-emerald-500/20 hover:border-emerald-500/40"
                      : isNext
                        ? "bg-[#D4A017]/5 border-[#D4A017]/30 hover:border-[#D4A017]/50"
                        : "bg-[#EDF1F8]/30 border-[#C9D4E6]/40 hover:border-[#C9D4E6]/60"
                  }`}
                  data-testid={`chapter-${ch.id}`}
                >
                  {/* Status Icon */}
                  <div className="shrink-0">
                    {ch.completed ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    ) : isNext ? (
                      <Play className="h-5 w-5 text-[#A87608]" />
                    ) : (
                      <Circle className="h-5 w-5 text-[#3A4557]" />
                    )}
                  </div>

                  {/* Chapter Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#3A4557]">Chapter {idx + 1}</span>
                      {isNext && <span className="text-[10px] bg-[#D4A017]/15 text-[#A87608] px-1.5 py-0.5 rounded-full font-medium">UP NEXT</span>}
                    </div>
                    <p className={`text-sm font-medium truncate ${ch.completed ? "text-emerald-700" : "text-[#1B2A4A]"}`}>
                      {ch.title}
                    </p>
                    <p className="text-xs text-[#3A4557] truncate">{ch.description}</p>
                  </div>

                  <ChevronRight className="h-4 w-4 text-[#3A4557] group-hover:text-[#1B2A4A] transition-colors shrink-0" />
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Quiz Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className={`rounded-2xl border p-5 ${
            allChaptersDone
              ? "bg-gradient-to-r from-[#D4A017]/10 to-[#D9A82C]/10 border-[#D4A017]/30"
              : "bg-[#EDF1F8]/30 border-[#C9D4E6]/50"
          }`}
          data-testid="quiz-section"
        >
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              allChaptersDone ? "bg-[#D4A017]/15" : "bg-[#E2E9F4]/50"
            }`}>
              {allChaptersDone ? (
                <Trophy className="h-6 w-6 text-[#A87608]" />
              ) : (
                <Lock className="h-6 w-6 text-[#3A4557]" />
              )}
            </div>
            <div className="flex-1">
              <p className={`font-semibold ${allChaptersDone ? "text-[#A87608]" : "text-[#3A4557]"}`}>
                Module Quiz
              </p>
              <p className="text-xs text-[#3A4557]">
                {allChaptersDone
                  ? "All chapters complete! Test your knowledge with 10 questions."
                  : `Complete all ${module.total_chapters} chapters to unlock the quiz`}
              </p>
              {module.quiz_score !== null && (
                <p className={`text-xs mt-1 font-medium ${module.quiz_passed ? "text-emerald-600" : "text-red-600"}`}>
                  Last score: {module.quiz_score}% {module.quiz_passed ? "— Passed!" : "— Need 70% to pass"}
                </p>
              )}
            </div>
            {allChaptersDone && (
              <button onClick={() => navigate(`/advisor/training/${moduleId}/quiz`)}
                className="bg-[#D4A017] hover:bg-[#D4A017] text-black font-medium text-sm px-4 py-2 rounded-lg transition-colors"
                data-testid="start-quiz-btn">
                {module.quiz_score !== null ? "Retake" : "Start Quiz"}
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
